import {Box, Typography} from '@mui/material';
import {useSelector} from 'react-redux';

import reduxSelectors from '../../../redux/selectors';

interface CameraGridProps {
  counts: Record<number, number>;
  machineName?: string | null;
  machineType?: string | null;
  onCameraClick?: (camId: number) => void;
}

interface GridCell {
  label: string;
  camId: number | null; // null = disabled (future slot)
  gridColumn: number;
  gridRow: number;
}

const GRID_CELLS: GridCell[] = [
  {label: 'Front Left', camId: null, gridColumn: 1, gridRow: 1},
  {label: 'Front', camId: 0, gridColumn: 2, gridRow: 1},
  {label: 'Front Right', camId: null, gridColumn: 3, gridRow: 1},
  {label: 'Left', camId: 2, gridColumn: 1, gridRow: 2},
  {label: 'MACHINE', camId: -1, gridColumn: 2, gridRow: 2}, // machine center
  {label: 'Right', camId: 3, gridColumn: 3, gridRow: 2},
  {label: 'Rear Left', camId: null, gridColumn: 1, gridRow: 3},
  {label: 'Rear', camId: 1, gridColumn: 2, gridRow: 3},
  {label: 'Rear Right', camId: null, gridColumn: 3, gridRow: 3},
];

export const CameraGrid = ({counts, machineName, machineType, onCameraClick}: CameraGridProps) => {
  const isDarkMode = useSelector(reduxSelectors.app.getIsDarkMode);

  return (
    <Box
      display="grid"
      gridTemplateColumns="repeat(3, 1fr)"
      gridTemplateRows="repeat(3, 1fr)"
      gap={1}
      height="100%"
      p={1}
    >
      {GRID_CELLS.map((cell) => {
        const isMachine = cell.camId === -1;
        const isDisabled = cell.camId === null;
        const count = cell.camId != null && !isMachine ? (counts[cell.camId] ?? 0) : null;

        if (isMachine) {
          return (
            <Box
              key={cell.label}
              sx={{
                gridColumn: cell.gridColumn,
                gridRow: cell.gridRow,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: '3px solid',
                borderColor: 'warning.main',
                bgcolor: 'warning.main',
                borderRadius: 1,
                p: 1,
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                color="warning.contrastText"
                textAlign="center"
                sx={{wordBreak: 'break-word'}}
              >
                {machineName && machineType
                  ? `${machineName} (${machineType})`
                  : machineName ?? 'MACHINE'}
              </Typography>
            </Box>
          );
        }

        if (isDisabled) {
          return (
            <Box
              key={cell.label}
              sx={{
                gridColumn: cell.gridColumn,
                gridRow: cell.gridRow,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid',
                borderColor: isDarkMode ? 'grey.700' : 'grey.300',
                bgcolor: isDarkMode ? 'grey.800' : 'grey.100',
                borderRadius: 1,
                p: 1,
                opacity: 0.5,
              }}
            >
              <Typography
                variant="caption"
                color="text.disabled"
                textAlign="center"
              >
                {cell.label}
              </Typography>
            </Box>
          );
        }

        // Active camera position
        const hasDetections = count != null && count > 0;
        const isClickable = hasDetections && !!onCameraClick;
        return (
          <Box
            key={cell.label}
            onClick={isClickable ? () => onCameraClick!(cell.camId!) : undefined}
            sx={{
              gridColumn: cell.gridColumn,
              gridRow: cell.gridRow,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid',
              borderColor: hasDetections ? 'error.main' : (isDarkMode ? 'grey.600' : 'grey.300'),
              bgcolor: hasDetections ? 'error.main' : (isDarkMode ? 'grey.800' : 'grey.200'),
              borderRadius: 1,
              p: 1,
              ...(isClickable && {
                cursor: 'pointer',
                '&:hover': {opacity: 0.85},
              }),
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              color={hasDetections ? 'error.contrastText' : 'text.secondary'}
              textAlign="center"
            >
              {cell.label}
            </Typography>
            <Typography
              variant="h5"
              fontWeight="bold"
              color={hasDetections ? 'error.contrastText' : 'text.secondary'}
            >
              {count ?? 0}
            </Typography>
            <Typography
              variant="caption"
              color={hasDetections ? 'error.contrastText' : 'text.disabled'}
              textAlign="center"
            >
              {cell.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};
